import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MediaPicker } from "@/components/admin/MediaPicker";

const inputCls = "bg-white border-zinc-300 text-zinc-800 focus-visible:ring-[#FF6B00] rounded-sm";

const BizField = ({ label, k, biz, setBiz, testid }) => (
  <div>
    <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">{label}</Label>
    <Input value={biz[k] || ""} onChange={(e) => setBiz((b) => ({ ...b, [k]: e.target.value }))} className={inputCls} data-testid={testid} />
  </div>
);

export default function AdminSettings() {
  const { user } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [biz, setBiz] = useState(null);
  const [savingBiz, setSavingBiz] = useState(false);

  const fetchBiz = useCallback(async () => {
    try {
      const r = await api.get("/admin/content/business");
      setBiz(r.data.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    fetchBiz();
  }, [fetchBiz]);

  const saveBiz = async (e) => {
    e.preventDefault();
    setSavingBiz(true);
    try {
      await api.put("/admin/content/business", { data: biz });
      toast.success("Business information saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingBiz(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password updated");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setSocial = (i, k, v) => setBiz((b) => ({ ...b, social: (b.social || []).map((s, idx) => (idx === i ? { ...s, [k]: v } : s)) }));

  return (
    <div data-testid="admin-settings">
      <h1 className="font-display text-2xl font-black uppercase text-zinc-900 mb-6">Settings</h1>

      {biz && (
        <form onSubmit={saveBiz} className="bg-white border border-zinc-200 p-6 max-w-3xl mb-6 space-y-4" data-testid="business-form">
          <div>
            <h2 className="font-display text-lg font-bold uppercase text-zinc-900">Business Information</h2>
            <p className="text-xs text-zinc-500 mt-1">Used for structured data (schema.org), contact details, and public business identity. Only real information is shown publicly.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <BizField label="Business Name" k="business_name" biz={biz} setBiz={setBiz} testid="biz-name" />
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500 block mb-1.5">Logo</Label>
              <MediaPicker value={biz.logo_url} onChange={(v) => setBiz((b) => ({ ...b, logo_url: v }))} testid="biz-logo" />
            </div>
            <BizField label="Phone" k="phone" biz={biz} setBiz={setBiz} testid="biz-phone" />
            <BizField label="Email" k="email" biz={biz} setBiz={setBiz} testid="biz-email" />
            <BizField label="Street Address" k="address" biz={biz} setBiz={setBiz} testid="biz-address" />
            <BizField label="City" k="city" biz={biz} setBiz={setBiz} testid="biz-city" />
            <BizField label="Province / State" k="province" biz={biz} setBiz={setBiz} testid="biz-province" />
            <BizField label="Postal Code" k="postal_code" biz={biz} setBiz={setBiz} testid="biz-postal" />
            <BizField label="Business Hours" k="hours" biz={biz} setBiz={setBiz} testid="biz-hours" />
            <BizField label="Primary Website URL" k="website_url" biz={biz} setBiz={setBiz} testid="biz-website" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500 block mb-2">Social Media Links</Label>
            <div className="space-y-2">
              {(biz.social || []).map((s, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`biz-social-row-${i}`}>
                  <Input placeholder="Label (e.g. Facebook)" value={s.label || ""} onChange={(e) => setSocial(i, "label", e.target.value)} className={`${inputCls} w-44`} data-testid={`biz-social-label-${i}`} />
                  <Input placeholder="https://..." value={s.url || ""} onChange={(e) => setSocial(i, "url", e.target.value)} className={`${inputCls} flex-1 font-mono text-xs`} data-testid={`biz-social-url-${i}`} />
                  <button type="button" onClick={() => setBiz((b) => ({ ...b, social: b.social.filter((_, idx) => idx !== i) }))} className="text-zinc-500 hover:text-red-600 shrink-0" aria-label="Remove social link" data-testid={`biz-social-remove-${i}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={() => setBiz((b) => ({ ...b, social: [...(b.social || []), { label: "", url: "" }] }))} className="border-zinc-300 text-zinc-600 rounded-sm text-xs uppercase font-bold" data-testid="biz-social-add">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Social Link
              </Button>
            </div>
          </div>
          <Button type="submit" disabled={savingBiz} className="bg-[#FF6B00] hover:bg-[#FF8533] text-black font-bold uppercase tracking-wider rounded-sm" data-testid="biz-save-button">
            {savingBiz ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Business Info"}
          </Button>
        </form>
      )}

      <div className="bg-white border border-zinc-200 p-6 max-w-lg mb-6">
        <h2 className="font-display text-lg font-bold uppercase text-zinc-900 mb-3">Account</h2>
        <p className="text-sm text-zinc-500">Signed in as <span className="text-zinc-900 font-medium" data-testid="settings-email">{user?.email}</span></p>
        <p className="text-xs text-zinc-500 mt-1">Role: {user?.role}</p>
      </div>

      <form onSubmit={submit} className="bg-white border border-zinc-200 p-6 max-w-lg space-y-4">
        <h2 className="font-display text-lg font-bold uppercase text-zinc-900">Change Password</h2>
        <div>
          <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Current Password</Label>
          <Input type="password" required value={form.current_password} onChange={set("current_password")} className={inputCls} data-testid="settings-current-password" />
        </div>
        <div>
          <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">New Password (min 8 characters)</Label>
          <Input type="password" required minLength={8} value={form.new_password} onChange={set("new_password")} className={inputCls} data-testid="settings-new-password" />
        </div>
        <div>
          <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Confirm New Password</Label>
          <Input type="password" required minLength={8} value={form.confirm} onChange={set("confirm")} className={inputCls} data-testid="settings-confirm-password" />
        </div>
        <Button type="submit" disabled={saving} className="bg-[#FF6B00] hover:bg-[#FF8533] text-black font-bold uppercase tracking-wider rounded-sm" data-testid="settings-save-password">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Password"}
        </Button>
      </form>
    </div>
  );
}
